import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { Breadcrumbs, Button, Divider, Link, Stack } from '@packages/shared';
import { ArrowMediumLeft32 } from '@packages/themes/icons';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import type { BreadcrumbData } from '@packages/gql/generated/shopping/graphql';
import { useConfig } from '@packages/utilities';
import {
  createFilteredQueryStringWithSameOrder,
  createSearchQueryString,
} from './convertProductToSearchUrl';
import { messages } from './messages';

// eslint-disable-next-line @typescript-eslint/naming-convention
const BreadcrumbsFragment = graphql(/* GraphQL */ `
  fragment BreadcrumbsFragment on Breadcrumb {
    breadCrumb {
      name
      id
      uriPath
    }
  }
`);

const BackButton = ({ breadCrumb }: { breadCrumb: Array<BreadcrumbData> }) => {
  const { formatMessage } = useIntl();
  const { query, asPath } = useRouter();
  const hasBreadcrumb = breadCrumb.length > 0;
  const [rememberedBackLink, setRememberedBackLink] = useState<string>(
    hasBreadcrumb
      ? `${breadCrumb[breadCrumb.length - 1].uriPath}${createFilteredQueryStringWithSameOrder(
          asPath,
        )}`
      : createSearchQueryString(asPath, query),
  );

  useEffect(() => {
    const pageData = window.sessionStorage.getItem('rememberedPageData');
    if (pageData) {
      const rememberedData = JSON.parse(pageData);
      setRememberedBackLink(rememberedData.path);
    }
  }, []);

  return (
    <Button LinkComponent={Link} href={rememberedBackLink} startIcon={<ArrowMediumLeft32 />}>
      {
        /** if there are no breadcrumbs (which is the case, if you come from a search result page - SERP),
         * there has to be a different button message
         * */
        hasBreadcrumb ? formatMessage(messages.backToMBA) : formatMessage(messages.backToSERP)
      }
    </Button>
  );
};

export type ExtendedBreadcrumbsProps = {
  /** The breadcrumbs object. */
  breadCrumbData: FragmentType<typeof BreadcrumbsFragment> | undefined | null;
};

/**
 * <p>This components provides a basic breadcrumbs navigation, to get back to the previous page.</p>
 * <p>In mobile, the breadcrumbs are condensed to 3 entries maximum.</p>
 */
export const ExtendedBreadcrumbs = ({ breadCrumbData }: ExtendedBreadcrumbsProps) => {
  const {
    host: { domain },
  } = useConfig();

  const breadCrumb = breadCrumbData
    ? getFragmentData(BreadcrumbsFragment, breadCrumbData).breadCrumb ?? []
    : [];
  const hasBreadcrumb = breadCrumb.length > 0;

  return (
    <>
      <Stack gap={1} direction="row" alignItems="center">
        <BackButton breadCrumb={breadCrumb} />
        {hasBreadcrumb && (
          <>
            <Breadcrumbs>
              {breadCrumb.map(({ id, uriPath, name }) => (
                <Link key={id} href={uriPath} underline="hover">
                  {name}
                </Link>
              ))}
            </Breadcrumbs>
            <script
              type="application/ld+json"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  itemListElement: breadCrumb.map(({ uriPath, name }, index) => ({
                    item: { name, '@id': `https://www.${domain}${uriPath}` },
                    '@type': 'ListItem',
                    position: index + 1,
                  })),
                  '@type': 'BreadcrumbList',
                  '@context': 'http://schema.org',
                }),
              }}
            />
          </>
        )}
      </Stack>
      <Divider sx={{ mt: 1.5, mb: 1 }} />
    </>
  );
};
